import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchEmployer360, type Employer360, type EmployerOffer360 } from '../lib/userDetailService';
import { disableAccount, resetPassword } from '../lib/userService';
import { logger } from '../lib/logger';
import AccountChangeHistory from '../components/AccountChangeHistory';
import { employeeRangeDisplay } from '../lib/employeeRanges';

const statusLabels: Record<string, string> = {
  active: 'Actif',
  pending_verification: 'En attente',
  suspended: 'Suspendu',
  rejected: 'Rejeté',
};
const statusClasses: Record<string, string> = {
  active: 'bg-blue-50 text-blue-700 border-blue-200',
  pending_verification: 'bg-amber-50 text-amber-700 border-amber-200',
  suspended: 'bg-red-50 text-red-700 border-red-200',
};
const offerStatusLabels: Record<string, string> = {
  draft: 'Brouillon', active: 'Active', paused: 'En pause', closed: 'Fermée', expired: 'Expirée', closed_by_admin: 'Fermée (admin)',
};
const videoStatusLabels: Record<string, string> = {
  uploading: 'Envoi', analyzing: 'Analyse IA', pending_review: 'À modérer',
  approved: 'Approuvée', rejected: 'Rejetée', ready: 'Prête',
};
const appStatusLabels: Record<string, string> = {
  pending: 'En attente', viewed: 'Vue', selected: 'Sélectionné', rejected: 'Refusée',
};
const durationLabels: Record<string, string> = {
  full_time_35h: 'Temps plein 35h', part_time: 'Temps partiel', full_time: 'Temps plein', both: 'Les deux',
};
const salaryTypeLabels: Record<string, string> = {
  fixed: 'Fixe', range: 'Fourchette', according_to_profile: 'Selon profil',
};
const videoTypeLabels: Record<string, string> = {
  company: 'Entreprise', offer: 'Offre', team: 'Équipe', workplace: 'Lieu de travail',
};
const companyFunctionLabels: Record<string, string> = {
  direction: 'Direction',
  finance_admin: 'Finance & Administration',
  rh: 'Ressources Humaines',
  commercial: 'Commercial & Clientèle',
  marketing: 'Marketing & Communication',
  tech: 'Tech & Informatique',
  achats_logistique: 'Achats & Logistique',
  production: 'Production & Terrain',
};

function companyFunctionLabel(value?: string | null): string {
  if (!value) return '';
  return companyFunctionLabels[value] || value;
}

function Card({ title, children, action }: { title: string; children: React.ReactNode; action?: React.ReactNode }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200">
      <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-800">{title}</h3>
        {action}
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <p className="text-[11px] uppercase tracking-wide text-gray-400 font-semibold">{label}</p>
      <div className="text-sm text-gray-800 mt-0.5 break-words">{value || '—'}</div>
    </div>
  );
}

function Chips({ items }: { items: string[] }) {
  if (!items.length) return <span className="text-gray-400">—</span>;
  return (
    <div className="flex flex-wrap gap-1 mt-0.5">
      {items.map((x) => (
        <span key={x} className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-700">{x}</span>
      ))}
    </div>
  );
}

function fmtSalary(o: EmployerOffer360) {
  if (o.salaryType === 'according_to_profile') return 'Selon profil';
  if (o.salaryMin != null && o.salaryMax != null) return `${o.salaryMin} – ${o.salaryMax} €`;
  if (o.salaryMin != null) return `à partir de ${o.salaryMin} €`;
  if (o.salaryMax != null) return `jusqu'à ${o.salaryMax} €`;
  return null;
}

const EmployerDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [e, setE] = useState<Employer360 | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [newPwd, setNewPwd] = useState('');
  const [expandedOfferId, setExpandedOfferId] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    fetchEmployer360(id)
      .then(setE)
      .catch((err) => {
        logger.error('load_employer_360', 'EmployerDetail', { error: String(err) });
        setError('Recruteur introuvable');
      })
      .finally(() => setLoading(false));
  }, [id]);

  const handleToggleSuspend = async () => {
    if (!e) return;
    setActionLoading(true);
    try {
      const willSuspend = e.status !== 'suspended';
      await disableAccount(e.userId, willSuspend);
      setE({ ...e, status: willSuspend ? 'suspended' : 'active' });
    } catch (err) {
      alert(`Erreur: ${err}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleResetPwd = async () => {
    if (!e || newPwd.length < 6) return;
    setActionLoading(true);
    try {
      await resetPassword(e.userId, newPwd);
      alert('Mot de passe réinitialisé.');
      setResetOpen(false);
      setNewPwd('');
    } catch (err) {
      alert(`Erreur: ${err}`);
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-[#00244F]/20 border-t-[#00244F] rounded-full animate-spin" />
      </div>
    );
  }
  if (error || !e) {
    return (
      <div className="text-center py-20">
        <p className="text-red-600 font-medium mb-4">{error || 'Erreur'}</p>
        <button onClick={() => navigate('/employers')} className="px-4 py-2 text-sm font-medium text-white bg-[#00244F] rounded-lg">
          Retour à la liste
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border border-gray-200 p-5 flex items-start gap-4">
        {e.establishments[0]?.logoUrl ? (
          <img src={e.establishments[0].logoUrl} alt="" className="w-16 h-16 rounded-xl object-cover" />
        ) : (
          <div className="w-16 h-16 rounded-xl bg-[#365A8C] flex items-center justify-center text-white text-xl font-bold">
            🏢
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-xl font-bold text-gray-900">{e.companyName || '—'}</h2>
            <span className={`px-2.5 py-0.5 text-xs font-medium rounded-full border ${statusClasses[e.status] || 'bg-gray-100 text-gray-500 border-gray-200'}`}>
              {statusLabels[e.status] || e.status}
            </span>
            {e.seedTag && <span className="px-1.5 py-0.5 text-[9px] font-bold rounded bg-indigo-100 text-indigo-700 uppercase">seed</span>}
            {e.plan === 'premium' ? (
              <span className="px-2 py-0.5 text-[11px] rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">Premium</span>
            ) : (
              <span className="px-2 py-0.5 text-[11px] rounded-full bg-gray-50 text-gray-500 border border-gray-200">Gratuit</span>
            )}
            {e.emailVerified && (
              <span className="px-2 py-0.5 text-[11px] rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">Email vérifié</span>
            )}
            {e.siretVerified && (
              <span className="px-2 py-0.5 text-[11px] rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">SIRET vérifié</span>
            )}
          </div>
          <p className="text-sm text-gray-500 mt-1">
            Contact : {e.firstName} {e.lastName}{e.positionInCompany ? ` (${companyFunctionLabel(e.positionInCompany)})` : ''}
          </p>
          <div className="flex items-center gap-4 mt-2 text-xs text-gray-500 flex-wrap">
            <span>✉️ {e.email || '—'}</span>
            <span>📞 {e.phone || '—'}</span>
            <span>📍 {[e.address, e.zipcode, e.city].filter(Boolean).join(', ') || '—'}</span>
            <span>Inscrit le {e.registeredAt}</span>
            <span>Dernière connexion : {e.lastLoginAt || 'jamais'}</span>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={handleToggleSuspend}
            disabled={actionLoading}
            className={`px-3 py-1.5 text-xs font-medium rounded-md ${
              e.status === 'suspended' ? 'text-blue-600 bg-blue-50 hover:bg-blue-100' : 'text-orange-600 bg-orange-50 hover:bg-orange-100'
            } disabled:opacity-40`}
          >
            {e.status === 'suspended' ? 'Réactiver' : 'Suspendre'}
          </button>
          <button
            onClick={() => { setResetOpen(true); setNewPwd(''); }}
            className="px-3 py-1.5 text-xs font-medium text-[#00244F] bg-blue-50 hover:bg-blue-100 rounded-md"
          >
            Réinit. mdp
          </button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        {[
          { label: 'Candidatures reçues', value: e.stats.applicationsReceived, icon: '📩' },
          { label: 'Profils consultés', value: e.stats.profileViewsMade, icon: '👁️' },
          { label: 'Favoris', value: e.stats.favoritesMade, icon: '⭐' },
          { label: 'Offres actives', value: e.stats.activeOffers, icon: '📋' },
          { label: 'Candidats contactés', value: e.stats.candidatesContacted, icon: '🤝' },
          { label: 'Vidéos vues', value: e.stats.videosWatched, icon: '🎬' },
          { label: 'Conversations', value: e.stats.conversations, icon: '💬' },
          { label: 'Abonnements', value: e.stats.following, icon: '➕' },
          { label: 'Abonnés', value: e.stats.followers, icon: '👥' },
        ].map((s) => (
          <div key={s.label} className="bg-white rounded-xl border border-gray-200 px-4 py-3 flex items-center gap-3">
            <span className="text-2xl">{s.icon}</span>
            <div>
              <p className="text-xl font-bold text-gray-900">{s.value}</p>
              <p className="text-xs text-gray-500">{s.label}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <Card title="Compte">
          <div className="grid grid-cols-2 gap-x-4 gap-y-3">
            <Field label="ID profil recruteur" value={<span className="font-mono text-xs">{e.profileId}</span>} />
            <Field label="ID utilisateur" value={<span className="font-mono text-xs">{e.userId}</span>} />
            <Field label="Rôle" value={e.role} />
            <Field label="Statut compte" value={statusLabels[e.status] || e.status} />
            <Field label="Langue préférée" value={e.preferredLang} />
            <Field label="Tag seed" value={e.seedTag} />
            <Field label="Inscrit le" value={e.registeredAt} />
            <Field label="Mis à jour" value={e.updatedAt} />
            <Field label="Dernière connexion" value={e.lastLoginAt} />
            <Field label="Token push" value={e.hasPushToken ? 'Oui' : 'Non'} />
            <Field label="Révision" value={e.revision != null ? String(e.revision) : null} />
            <Field label="Hash contenu" value={e.contentHash ? <span className="font-mono text-[11px] break-all">{e.contentHash}</span> : null} />
          </div>
        </Card>

        <Card title="Identité contact">
          <div className="grid grid-cols-2 gap-x-4 gap-y-3">
            <Field label="Prénom" value={e.firstName} />
            <Field label="Nom" value={e.lastName} />
            <Field label="Fonction" value={companyFunctionLabel(e.positionInCompany)} />
            <Field label="Email compte" value={e.email} />
            <Field label="Téléphone compte" value={e.phone} />
            <Field label="Email professionnel" value={e.employerEmail} />
            <Field label="Téléphone professionnel" value={e.employerPhone} />
            <Field label="Email vérifié" value={e.emailVerified ? (e.emailVerifiedAt ? `Oui · ${e.emailVerifiedAt}` : 'Oui') : 'Non'} />
          </div>
        </Card>
      </div>

      <Card title="Entreprise">
        <div className="grid grid-cols-4 gap-x-4 gap-y-3">
          <Field label="Raison sociale" value={e.companyName} />
          <Field label="SIRET" value={e.siret ? `${e.siret}${e.siretVerified ? ' ✓ vérifié' : ' (non vérifié)'}` : null} />
          <Field label="Effectif" value={employeeRangeDisplay(e.employeeRange, e.employeeCount)} />
          <Field label="Profil complet" value={e.profileComplete ? 'Oui' : 'Non'} />
          <Field label="Adresse" value={e.address} />
          <Field label="Code postal" value={e.zipcode} />
          <Field label="Ville" value={e.city} />
          <Field label="Abonnement actif" value={e.subscriptionActive ? 'Oui' : 'Non'} />
          <Field label="Client Stripe (profil)" value={e.stripeCustomerId} />
        </div>
      </Card>

      <Card title={`Établissements (${e.establishments.length})`}>
        {e.establishments.length === 0 ? (
          <p className="text-sm text-gray-400">Aucun établissement</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {e.establishments.map((est) => (
              <div key={est.id} className="border border-gray-100 rounded-lg p-3 flex gap-3">
                {est.logoUrl ? (
                  <img src={est.logoUrl} alt="" className="w-14 h-14 rounded-lg object-cover flex-shrink-0" />
                ) : (
                  <div className="w-14 h-14 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0 text-lg">🏪</div>
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-800">{est.name || '—'}</p>
                  <p className="text-xs text-gray-500">{[est.zipcode, est.city].filter(Boolean).join(' ') || '—'}</p>
                  {est.locationText && <p className="text-[11px] text-gray-400 font-mono mt-0.5">{est.locationText}</p>}
                  <p className="text-[11px] text-gray-400 mt-1">Créé le {est.createdAt}{est.updatedAt ? ` · MAJ ${est.updatedAt}` : ''}</p>
                  {est.photos.length > 0 && (
                    <div className="flex gap-1 mt-2 flex-wrap">
                      {est.photos.map((url) => (
                        <a key={url} href={url} target="_blank" rel="noreferrer">
                          <img src={url} alt="" className="w-10 h-10 rounded object-cover" />
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card title={`Offres (${e.offers.length})`}>
        {e.offers.length === 0 ? (
          <p className="text-sm text-gray-400">Aucune offre</p>
        ) : (
          <div className="space-y-2">
            {e.offers.map((o) => {
              const open = expandedOfferId === o.id;
              return (
                <div key={o.id} className="border border-gray-100 rounded-lg">
                  <button
                    type="button"
                    onClick={() => setExpandedOfferId(open ? null : o.id)}
                    className="w-full text-left px-3 py-2.5 flex items-center gap-3 hover:bg-gray-50"
                  >
                    <span className="text-gray-400 text-xs w-4">{open ? '▼' : '▶'}</span>
                    <span className="flex-1 font-medium text-sm text-gray-800">{o.positionName || o.positionCode || '—'}</span>
                    <span className="text-xs text-gray-500 hidden sm:inline">{o.establishmentName} · {o.city}</span>
                    <span className="text-xs text-gray-500 uppercase">{o.contractType || '—'}</span>
                    <span className={`px-2 py-0.5 text-xs rounded-full ${o.status === 'active' ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-600'}`}>
                      {offerStatusLabels[o.status] || o.status}
                    </span>
                    <span className="text-xs text-gray-500">{o.applicationsCount} cand.</span>
                  </button>
                  {open && (
                    <div className="px-4 pb-4 pt-1 border-t border-gray-100">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-3">
                        <Field label="ID offre" value={<span className="font-mono text-xs">{o.id}</span>} />
                        <Field label="Code secteur" value={o.sectorCode} />
                        <Field label="Code poste" value={o.positionCode} />
                        <Field label="Statut" value={offerStatusLabels[o.status] || o.status} />
                        <Field label="Établissement" value={o.establishmentName} />
                        <Field label="Adresse" value={o.establishmentAddress} />
                        <Field label="CP" value={o.establishmentZipcode} />
                        <Field label="Ville" value={o.city} />
                        <Field label="Contrat" value={o.contractType?.toUpperCase()} />
                        <Field label="Début" value={o.contractStartDate} />
                        <Field label="Fin" value={o.contractEndDate} />
                        <Field label="Expérience" value={o.yearsExperience} />
                        <Field label="Durée" value={o.workDuration ? (durationLabels[o.workDuration] || o.workDuration) : null} />
                        <Field label="Salaire" value={fmtSalary(o)} />
                        <Field label="Type salaire" value={o.salaryType ? (salaryTypeLabels[o.salaryType] || o.salaryType) : null} />
                        <Field label="Permis requis" value={o.requiredDrivingLicense ? 'Oui' : 'Non'} />
                        <Field label="Publiée le" value={o.publishedAt} />
                        <Field label="Créée le" value={o.createdAt} />
                        <Field label="Mise à jour" value={o.updatedAt} />
                        <Field label="Candidatures" value={String(o.applicationsCount)} />
                        <Field label="Seed" value={o.seedTag} />
                        <Field label="Révision" value={o.revision != null ? String(o.revision) : null} />
                      </div>
                      <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                        <Field label="Rythme" value={<Chips items={o.workRhythm} />} />
                        <Field label="Diplômes requis" value={<Chips items={o.requiredDiplomas} />} />
                        <Field label="Avantages" value={<Chips items={o.advantages} />} />
                        <Field
                          label="Langues requises"
                          value={o.requiredLanguages.length
                            ? o.requiredLanguages.map((l) => `${l.language} (${l.level}/5)`).join(', ')
                            : null}
                        />
                      </div>
                      <div className="mt-3">
                        <Field label="Compétences requises" value={<Chips items={o.requiredSkills} />} />
                      </div>
                      {o.otherInfo && (
                        <div className="mt-3">
                          <p className="text-[11px] uppercase tracking-wide text-gray-400 font-semibold">Autres infos</p>
                          <p className="text-sm text-gray-800 mt-0.5 whitespace-pre-wrap">{o.otherInfo}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>

      <Card
        title={`Vidéos (${e.videos.length})`}
        action={
          <button onClick={() => navigate('/videos')} className="text-xs text-[#00244F] hover:underline">
            Ouvrir la validation contenu média →
          </button>
        }
      >
        {e.videos.length === 0 ? (
          <p className="text-sm text-gray-400">Aucune vidéo</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {e.videos.map((v) => (
              <div key={v.id} className="border border-gray-100 rounded-lg overflow-hidden">
                <a href={v.videoUrl || undefined} target={v.videoUrl ? '_blank' : undefined} rel="noreferrer" className="block">
                  <div className="h-40 bg-gray-100 relative">
                    {v.thumbnailUrl && <img src={v.thumbnailUrl} alt="" className="w-full h-full object-cover" />}
                    <span className="absolute bottom-1 left-1 px-1.5 py-0.5 text-[10px] font-medium rounded bg-black/60 text-white">
                      {videoStatusLabels[v.status] || v.status}
                    </span>
                    {v.durationSeconds != null && (
                      <span className="absolute bottom-1 right-1 px-1.5 py-0.5 text-[10px] rounded bg-black/60 text-white">
                        {v.durationSeconds}s
                      </span>
                    )}
                  </div>
                </a>
                <div className="p-3 space-y-1">
                  <p className="text-sm font-medium text-gray-800 truncate">{v.title || 'Sans titre'}</p>
                  <p className="text-xs text-gray-500">
                    {[videoTypeLabels[v.videoType || ''] || v.videoType, v.mediaType, v.createdAt].filter(Boolean).join(' · ')}
                  </p>
                  {v.analysisStatus && (
                    <p className="text-xs text-gray-500">
                      Analyse : {v.analysisStatus}{v.analysisVerdict ? ` · ${v.analysisVerdict}` : ''}
                    </p>
                  )}
                  {v.textOverlay && <p className="text-xs text-gray-600 italic">« {v.textOverlay} »</p>}
                  {v.tags.length > 0 && <Chips items={v.tags} />}
                  {v.offerId && <p className="text-[11px] font-mono text-gray-400">offre {v.offerId}</p>}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card title={`Candidatures reçues (${e.applications.length})`}>
        {e.applications.length === 0 ? (
          <p className="text-sm text-gray-400">Aucune candidature</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-gray-400">
                <th className="pb-2">Candidat</th>
                <th className="pb-2">Poste</th>
                <th className="pb-2">Statut</th>
                <th className="pb-2">Candidature</th>
                <th className="pb-2">Vue</th>
                <th className="pb-2">Sélection</th>
                <th className="pb-2"></th>
              </tr>
            </thead>
            <tbody>
              {e.applications.map((a) => (
                <tr key={a.id} className="border-t border-gray-100">
                  <td className="py-2 font-medium text-gray-800">{a.candidateName}</td>
                  <td className="py-2 text-gray-600">{a.positionName}</td>
                  <td className="py-2">
                    <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600">
                      {appStatusLabels[a.status] || a.status}
                    </span>
                  </td>
                  <td className="py-2 text-gray-500">{a.appliedAt}</td>
                  <td className="py-2 text-gray-500">{a.viewedAt || '—'}</td>
                  <td className="py-2 text-gray-500">{a.selectedAt || '—'}</td>
                  <td className="py-2 text-right">
                    <button onClick={() => navigate(`/candidates/${a.candidateId}`)} className="text-xs text-[#00244F] hover:underline">
                      Fiche →
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Card title={`Sélections (${e.selections.length})`}>
        {e.selections.length === 0 ? (
          <p className="text-sm text-gray-400">Aucun candidat sélectionné</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-gray-400">
                <th className="pb-2">Candidat</th>
                <th className="pb-2">Poste</th>
                <th className="pb-2">Date</th>
                <th className="pb-2"></th>
              </tr>
            </thead>
            <tbody>
              {e.selections.map((s) => (
                <tr key={s.id} className="border-t border-gray-100">
                  <td className="py-2 font-medium text-gray-800">{s.candidateName}</td>
                  <td className="py-2 text-gray-600">{s.positionName || '—'}</td>
                  <td className="py-2 text-gray-500">{s.selectedAt}</td>
                  <td className="py-2 text-right">
                    <button onClick={() => navigate(`/candidates/${s.candidateId}`)} className="text-xs text-[#00244F] hover:underline">
                      Fiche →
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card title={`Abonnements (${e.subscriptions.length})`}>
          {e.subscriptions.length === 0 ? (
            <p className="text-sm text-gray-400">Aucun abonnement</p>
          ) : (
            <div className="space-y-3">
              {e.subscriptions.map((s) => (
                <div key={s.id} className="border border-gray-100 rounded-lg p-3 grid grid-cols-2 gap-x-3 gap-y-2">
                  <Field label="Plan" value={`${s.plan} · ${s.status}`} />
                  <Field label="Montant HT" value={s.amountHt != null ? `${s.amountHt} €` : null} />
                  <Field label="Paiement" value={s.paymentMethod} />
                  <Field label="Résiliation en fin de période" value={s.cancelAtPeriodEnd ? 'Oui' : 'Non'} />
                  <Field label="Période" value={[s.currentPeriodStart, s.currentPeriodEnd].filter(Boolean).join(' → ') || null} />
                  <Field label="Créé le" value={s.createdAt} />
                  <Field label="Stripe customer" value={s.stripeCustomerId} />
                  <Field label="Stripe subscription" value={s.stripeSubscriptionId} />
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card title={`Factures (${e.invoices.length})`}>
          {e.invoices.length === 0 ? (
            <p className="text-sm text-gray-400">Aucune facture</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-gray-400">
                  <th className="pb-2">Montant</th>
                  <th className="pb-2">Statut</th>
                  <th className="pb-2">Période</th>
                  <th className="pb-2">Payée</th>
                  <th className="pb-2"></th>
                </tr>
              </thead>
              <tbody>
                {e.invoices.map((i) => (
                  <tr key={i.id} className="border-t border-gray-100">
                    <td className="py-2 font-medium">{i.amount != null ? `${i.amount} €` : '—'}</td>
                    <td className="py-2 text-gray-600">{i.status}</td>
                    <td className="py-2 text-gray-500">{[i.periodStart, i.periodEnd].filter(Boolean).join(' → ') || '—'}</td>
                    <td className="py-2 text-gray-500">{i.paidAt || '—'}</td>
                    <td className="py-2 text-right">
                      {i.pdfUrl && (
                        <a href={i.pdfUrl} target="_blank" rel="noreferrer" className="text-xs text-[#00244F] hover:underline">PDF</a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card title={`Abonnements sociaux (${e.following.length})`}>
          {e.following.length === 0 ? (
            <p className="text-sm text-gray-400">Aucun suivi</p>
          ) : (
            <ul className="space-y-1.5">
              {e.following.map((f) => (
                <li key={f.userId} className="text-sm text-gray-700 flex justify-between gap-2">
                  <span className="truncate">{f.email} <span className="text-gray-400">({f.role})</span></span>
                  <span className="text-xs text-gray-400 flex-shrink-0">{f.createdAt}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
        <Card title={`Abonnés (${e.followers.length})`}>
          {e.followers.length === 0 ? (
            <p className="text-sm text-gray-400">Aucun abonné</p>
          ) : (
            <ul className="space-y-1.5">
              {e.followers.map((f) => (
                <li key={f.userId} className="text-sm text-gray-700 flex justify-between gap-2">
                  <span className="truncate">{f.email} <span className="text-gray-400">({f.role})</span></span>
                  <span className="text-xs text-gray-400 flex-shrink-0">{f.createdAt}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
        <Card title={`Bloqués (${e.blocked.length})`}>
          {e.blocked.length === 0 ? (
            <p className="text-sm text-gray-400">Aucun blocage</p>
          ) : (
            <ul className="space-y-1.5">
              {e.blocked.map((b) => (
                <li key={b.userId} className="text-sm text-gray-700 flex justify-between gap-2">
                  <span className="truncate">{b.email}</span>
                  <span className="text-xs text-gray-400 flex-shrink-0">{b.createdAt}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      <Card title={`Conversations (${e.conversations.length})`}>
        {e.conversations.length === 0 ? (
          <p className="text-sm text-gray-400">Aucune conversation</p>
        ) : (
          <ul className="space-y-2">
            {e.conversations.map((c) => (
              <li key={c.id} className="border border-gray-100 rounded-lg px-3 py-2">
                <p className="text-sm font-medium text-gray-800">{c.otherEmail || '—'}</p>
                <p className="text-sm text-gray-600">{c.preview || '(aucun message)'}</p>
                <p className="text-[11px] text-gray-400 mt-0.5">{c.lastMessageAt || '—'}</p>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <AccountChangeHistory entries={e.changeLog} />

      {resetOpen && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50" onClick={() => setResetOpen(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4" onClick={(ev) => ev.stopPropagation()}>
            <div className="px-6 py-4 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900">Réinitialiser le mot de passe</h3>
              <p className="text-sm text-gray-500 mt-1">{e.firstName} {e.lastName} ({e.email})</p>
            </div>
            <div className="p-6">
              <input
                type="text"
                value={newPwd}
                onChange={(ev) => setNewPwd(ev.target.value)}
                placeholder="Nouveau mot de passe (min. 6 caractères)"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#00244F]/20"
                autoFocus
              />
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3 bg-gray-50 rounded-b-2xl">
              <button onClick={() => setResetOpen(false)} className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-200 hover:bg-gray-300 rounded-lg">
                Annuler
              </button>
              <button
                onClick={handleResetPwd}
                disabled={newPwd.length < 6 || actionLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-[#00244F] hover:bg-[#001B3D] rounded-lg disabled:opacity-40"
              >
                Confirmer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployerDetail;
